import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import Navbar from "@/components/Navbar";
import FleetGrid from "@/components/FleetGrid";
import SeoLongform from "@/components/SeoLongform";
import GeoAnswerBlock from "@/components/GeoAnswerBlock";
import { Phone, Sparkles, MapPin } from "lucide-react";
import { CITY_SEO } from "@/data/citySeo";

export const Route = createFileRoute("/fleet")({
  component: FleetPage,
  head: () => ({
    meta: [
      { title: "Configure Your Van — Sprinter Van Rentals Fleet" },
      { name: "description", content: "Choose your van: Mercedes Sprinter, Mercedes Executive, Chevy Express, or Ram ProMaster. Configure passengers, roof, and TV options." },
      { property: "og:title", content: "Configure Your Van — Sprinter Van Rentals" },
      { property: "og:description", content: "Build the exact passenger van you need — capacity, roof height, entertainment." },
    ],
    links: [{ rel: "canonical", href: "https://sprinter-van-rentals.com/fleet" }],
  }),
});

function FleetPage() {
  const [city, setCity] = useState<string>("orlando");
  const cityData = CITY_SEO[city];
  const cities = Object.values(CITY_SEO).sort((a, b) => a.label.localeCompare(b.label));

  return (
    <main className="min-h-screen bg-[#FFF8E7]">
      <Navbar />

      <section className="pt-32 pb-12 max-w-7xl mx-auto px-6">
        <p className="inline-flex items-center gap-2 text-[#F97316] font-display font-bold text-xs uppercase tracking-[0.3em] mb-4">
          <Sparkles size={14} /> The Configurable Fleet
        </p>
        <h1 className="font-display font-extrabold text-5xl md:text-6xl tracking-tight text-[#1a1a1a]">
          Build Your Van.
        </h1>
        <p className="mt-4 max-w-2xl text-lg text-[#1a1a1a]/75">
          Pick the family. Choose your passenger count, roof height, and entertainment.
          We'll show you the exact configuration with photos, diagram, and cargo space.
        </p>
        <div className="mt-6">
          <a href="tel:9713282665" className="inline-flex items-center gap-2 bg-[#F97316] text-[#0F172A] font-display font-bold uppercase text-xs tracking-widest px-6 py-3 rounded-full">
            <Phone size={14} /> Reserve · (971) 328-2665
          </a>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 pb-24">
        <div className="mb-8 border rounded-2xl p-5 md:p-6 bg-black/[0.02] border-black/10">
          <p className="font-display font-bold text-[10px] uppercase tracking-[0.3em] mb-3 text-black/55">
            <MapPin size={12} className="inline-block mr-1.5 -mt-0.5" /> Select your city
          </p>
          <div className="flex flex-wrap gap-2">
            {cities.map((c) => {
              const active = c.slug === city;
              return (
                <button
                  key={c.slug}
                  type="button"
                  onClick={() => setCity(c.slug)}
                  className={`font-display font-bold text-[11px] uppercase tracking-widest px-4 py-2 rounded-full border transition ${
                    active
                      ? "text-[#0F172A] border-transparent bg-[#F97316]"
                      : "bg-white text-black/75 border-black/10 hover:bg-black/5"
                  }`}
                >
                  {c.shortName}
                </button>
              );
            })}
          </div>
          <p className="mt-3 text-sm text-black/65">
            Showing live availability and pricing for vans in{" "}
            <span className="font-bold text-[#F97316]">{cityData?.label ?? city}</span>.
          </p>
        </div>

        <FleetGrid city={city} />

        <p className="mt-10 text-center text-[#1a1a1a]/60 text-sm">
          Looking for a city-specific page?{" "}
          <Link to="/orlando" className="text-[#F97316] font-bold underline">Orlando</Link> ·{" "}
          <Link to="/losangeles" className="text-[#F97316] font-bold underline">Los Angeles</Link> ·{" "}
          <Link to="/vegas" className="text-[#F97316] font-bold underline">Las Vegas</Link> ·{" "}
          <Link to="/phoenix" className="text-[#F97316] font-bold underline">Sprinter van rentals in Phoenix</Link>
        </p>
      </section>

      <SeoLongform
        title="Our nationwide passenger-van fleet"
        paragraphs={[
          "Every vehicle in our lineup is chosen for one job: moving groups of 7 to 15 people, and their luggage, comfortably and reliably. The Mercedes-Benz Sprinter is the reference-class large passenger van in North America — a 3.0L turbo-diesel platform with high-roof stand-up interior, captain's-chair configurations, and best-in-class ride quality. The Ford Transit is our value pick, seating the same 12 to 15 passengers on a 3.5L EcoBoost gas engine with lower daily rates and excellent parts availability. Premium minivans (Honda Odyssey, Toyota Sienna) round out the fleet for smaller groups of 7 who need family-friendly features like power sliding doors, integrated child anchors, and easy loading heights.",
          "Configuration matters as much as make and model. A 12-passenger Sprinter with three rows of bench seating carries very different luggage than a 15-passenger high-roof with four rows and a rear cargo shelf. Executive Sprinters swap in captain's chairs, rear entertainment screens, and premium leather for wedding parties and corporate clients. Every listing on this page shows the exact interior layout, seating count, luggage capacity in cubic feet, and daily rate so you can pick the right van before you get to the pickup lot.",
          "All vehicles are late-model, professionally detailed between rentals, and delivered with a full tank of fuel and unlimited-mileage options at checkout. Optional insurance, additional drivers, child car seats, and door-to-door delivery are available at the reservation step with transparent per-day pricing. We guarantee the specific vehicle you book — not a rental class — so the van you see on this page is the van you drive off the lot.",
        ]}
      />
      <GeoAnswerBlock />

      <footer className="py-8 text-center border-t border-[#F97316]/20">

        <p className="text-muted-foreground text-xs tracking-widest uppercase">&copy; 2026 Sprinter Van Rentals</p>
      </footer>
    </main>
  );
}
